import React, { useState, useEffect } from 'react';
import { AdventureInvite } from 'coop-adventure-engine';
import adventureInvitesService from '../../services/adventureInvitesService';
import apiService from '../../services/api';
import { AdventureTitleImage } from './AdventureTitleImage';
import { CharacterPortrait } from './CharacterPortrait';
import { AdventureHeader } from '../../types';

export interface AdventureInviteCardProps {
  /**
   * Adventure invite ID (required when invite is not provided)
   */
  inviteId?: string;
  
  /**
   * Adventure invite object (preferred - avoids redundant fetch and race conditions).
   * When provided, inviteId is derived from invite.id.
   */
  invite?: AdventureInvite;
  
  /**
   * Optional click handler
   */
  onClick?: () => void;
  
  /**
   * If true, renders without the outer frame (for use inside buttons)
   */
  noFrame?: boolean;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Custom styles
   */
  style?: React.CSSProperties;
}

/**
 * Component for displaying an adventure invite card
 * Shows adventure title image, "invited by" text, and inviter's portrait/name
 * Simplified design matching friend request cards
 */
export const AdventureInviteCard: React.FC<AdventureInviteCardProps> = ({
  inviteId: inviteIdProp,
  invite: inviteProp,
  onClick,
  noFrame = false,
  className = '',
  style
}) => {
  const effectiveInviteId = inviteProp?.id ?? inviteIdProp ?? '';

  const [invite, setInvite] = useState<AdventureInvite | null>(inviteProp ?? null);
  const [adventureHeader, setAdventureHeader] = useState<AdventureHeader | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviterName, setInviterName] = useState<string>('');
  const [inviterPortraitId, setInviterPortraitId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const loadInviteData = async () => {
      let foundInvite: AdventureInvite;

      if (inviteProp) {
        foundInvite = inviteProp;
        setInvite(foundInvite);
      } else if (inviteIdProp) {
        setIsLoading(true);
        setError(null);

        try {
          const invites = await adventureInvitesService.getAdventureInvites();
          const fetchedInvite = invites.find(inv => inv.id === inviteIdProp);
          if (!fetchedInvite) {
            throw new Error('Adventure invite not found');
          }
          foundInvite = fetchedInvite;
          setInvite(foundInvite);
        } catch (err) {
          console.error('Failed to load adventure invite:', err);
          setError(err instanceof Error ? err.message : 'Failed to load adventure invite');
          setIsLoading(false);
          return;
        }
      } else {
        setError('Adventure invite not found');
        setIsLoading(false);
        return;
      }

      try {
        const header = await apiService.getAdventureHeader(foundInvite.adventureId);
        setAdventureHeader(header);
      } catch (err) {
        console.error(`Failed to load adventure header for ${foundInvite.adventureId}:`, err);
      }

      try {
        const userInfo = await apiService.getUserInfo(foundInvite.inviterId);
        setInviterName(userInfo.playerName || userInfo.username || `User ${foundInvite.inviterId.substring(0, 8)}`);
        setInviterPortraitId(userInfo.portraitId);
      } catch (err) {
        console.error(`Failed to load inviter info for ${foundInvite.inviterId}:`, err);
        setInviterName(`User ${foundInvite.inviterId.substring(0, 8)}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadInviteData();
  }, [inviteIdProp, inviteProp]);

  if (isLoading) {
    return (
      <div
        className={`adventure-invite-card loading ${className}`}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '0.75rem',
          padding: '1rem',
          ...style
        }}
        data-testid={`adventure-invite-card-${effectiveInviteId}-loading`}
      >
        <div className="text-center py-4">
          <p className="text-gray-400 italic text-sm">Loading adventure invite...</p>
        </div>
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div
        className={`adventure-invite-card error ${className}`}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          borderRadius: '0.75rem',
          padding: '1rem',
          ...style
        }}
        data-testid={`adventure-invite-card-${effectiveInviteId}-error`}
      >
        <div className="text-center py-4">
          <p className="text-red-400 text-sm">
            {error || 'Adventure invite not found'}
          </p>
        </div>
      </div>
    );
  }

  const cardClasses = `
    adventure-invite-card
    ${onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}
    ${className}
  `.trim();

  const content = (
    <div className="flex flex-col gap-3 w-full">
      {/* Adventure Title Image */}
      <div 
        className="w-full mb-2"
        style={noFrame ? {} : { marginLeft: '-1rem', marginRight: '-1rem', marginTop: '-1rem' }}
      >
        <AdventureTitleImage
          titleImageId={adventureHeader?.titleImageId}
          variant="card"
          alt={adventureHeader?.title || 'Adventure'}
          responsive={true}
        />
      </div>
      
      {/* Invited by text */}
      <p className="text-xs text-gray-400 italic" style={{ textAlign: 'center', width: '100%' }}>Invited by</p>
      
      {/* Inviter Portrait and Name - horizontal layout, centered */}
      <div className="flex items-center justify-center w-full" style={{ gap: '1rem' }}>
        <div className="flex-shrink-0">
          <CharacterPortrait
            portraitId={inviterPortraitId}
            variant="thumbnail"
            alt={inviterName}
          />
        </div>
        <span className="text-base text-white">
          {inviterName}
        </span>
      </div>
    </div>
  );

  if (noFrame) {
    return (
      <div
        data-testid={`adventure-invite-card-${effectiveInviteId}`}
        className={cardClasses}
        style={style}
        onClick={onClick}
      >
        {content}
      </div>
    );
  }

  return (
    <div
      data-testid={`adventure-invite-card-${effectiveInviteId}`}
      className={cardClasses}
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '0.75rem',
        padding: '1rem',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)',
        ...style
      }}
      onClick={onClick}
    >
      {content}
    </div>
  );
};

export default AdventureInviteCard;

